import { CalendarIcon } from "@heroicons/react/24/outline";
import { format } from "date-fns";
import { de } from "date-fns/locale";

import type { CalendarActions, CalendarDay } from "~/modules/dashboard/components/TimeTrackingCalendar/calendarDays.ts";
import { HolidayAndAbsenceBars } from "~/modules/dashboard/components/TimeTrackingCalendar/HolidayAndAbsenceBars.tsx";
import { TrackingCard } from "~/modules/dashboard/components/TimeTrackingCalendar/TrackingCard.tsx";
import { TrackingRow } from "~/modules/dashboard/components/TimeTrackingCalendar/TrackingRow.tsx";
import { AddTimeTrackingButton } from "~/modules/timeTracking/components/components/AddTimeTrackingButton.tsx";

type Props = CalendarActions & {
	day: CalendarDay;
	// The phone body shows the "+" as a primary button.
	primaryAdd?: boolean;
	// The style of "Keine Einträge" on a day without entries: plain text in the month view, a block in the day view. None while the data loads.
	emptyHint?: "text" | "block";
};

/** The selected day below the compact grid: its holiday, its absences, its trackings and its "+". */
export function DayList(props: Props) {
	const {
		absenceActions,
		day,
		primaryAdd,
		emptyHint,
		canAddTrackingOn,
		canAddAbsence,
		showDisabledAddTracking,
		openCreate,
		openUpdate,
		openDelete,
		selectedTimeTrackingId,
		setSelectedTimeTrackingId,
	} = props;
	const canAddEntry = canAddAbsence || canAddTrackingOn(day.date);
	const close = () => setSelectedTimeTrackingId(null);
	const isEmpty = day.timeTrackings.length === 0 && day.absences.length === 0;

	return (
		<div className="flex min-w-0 flex-col gap-2">
			{/* From `md`, the selected `DayColumn` shows the date. Below `md`, the compact grid title shows only the month, so the date stays here. */}
			<h4 className="text-sm font-semibold text-gray-700 md:hidden">{format(day.date, "EEEE, d. MMMM", { locale: de })}</h4>
			<HolidayAndAbsenceBars day={day}
							   absenceActions={absenceActions}
							   inPlace />
			{isEmpty && emptyHint === "text" && <p className="text-sm text-gray-500">Keine Einträge</p>}
			{isEmpty && emptyHint === "block" && (
				<div className="flex flex-col items-center gap-2 py-8 text-gray-500">
					<CalendarIcon className="size-8 text-gray-400"
								  aria-hidden="true" />
					<p className="text-sm font-medium">Keine Einträge</p>
				</div>
			)}
			{day.timeTrackings.map((timeTracking) => timeTracking.id === selectedTimeTrackingId ? (
				<TrackingCard key={timeTracking.id}
							  timeTracking={timeTracking}
							  onClickEdit={() => {
								  close();
								  openUpdate(timeTracking.id);
							  }}
							  onClickDelete={() => {
								  close();
								  openDelete(timeTracking.id);
							  }}
							  onClickClose={close} />
			) : (
				<TrackingRow key={timeTracking.id}
							 timeTracking={timeTracking}
							 onClick={() => setSelectedTimeTrackingId(timeTracking.id)} />
			))}
			{(canAddEntry || showDisabledAddTracking) && (
				<AddTimeTrackingButton date={day.date}
									   canAddTracking={canAddEntry}
									   openCreate={openCreate}
									   className={primaryAdd ? "mt-3" : undefined}
									   primary={primaryAdd} />
			)}
		</div>
	);
}
